import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { RegionSetStatusType, SpecialtyTypeEnum } from "@app/projects/enumerators";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";

export interface MultiMapExportRouteMatchParams {
  projectId: string;
  projectRunConfigurationId: string;
}

export interface MultiMapExportRouteState {
  readonly selectedCountyStatuses?: readonly RegionSetStatusType[];
  readonly selectedProviderSources?: readonly ProviderNetworkTypeEnum[];
  readonly selectedServiceArea?: string;
  readonly selectedServiceAreaCountyCode?: string;
  readonly selectedServiceAreaStateCode?: string;
  readonly selectedSpecialtyGroupCode?: string;
  readonly selectedSpecialtyGroupName?: string;
}

/** One county of the run configuration's service area, with the status the County Status filter matches on. */
export interface MultiMapExportCounty {
  /** The code the export filters counties on. Not interchangeable with a region set id. */
  readonly countySsa: string;
  readonly name: string;
  readonly regionSetId: number;
  readonly status: RegionSetStatusType;
}

/** The run configuration's counties grouped by state, as the Counties selector renders them. */
export interface MultiMapExportStateGroup {
  readonly counties: readonly MultiMapExportCounty[];
  readonly stateAbbreviation: string;
  readonly stateName: string;
}

/**
 * The member point layers of the Map Content section. Providers reuse ProvidersFilterOptions so the
 * vocabulary matches what the Network Coverage Map plots; members have no counterpart there, where they
 * are two booleans on NetworkCoverageMapDataContext.
 */
export enum MultiMapExportMemberLayer {
  WithAccess = "withAccess",
  WithoutAccess = "withoutAccess",
}

/** One Map Content checkbox row: what it selects, its label, and the swatch matching the map's own pin. */
export interface MultiMapExportContentOption<TValue> {
  readonly label: string;
  /** Only the adequacy outcomes outline their swatch; map layers take their border from the icon. */
  readonly swatchBorderColor?: string;
  readonly swatchColor: string;
  readonly swatchIcon: string;
  readonly value: TValue;
}

/**
 * A Map Content column group: a parent checkbox that selects or clears its own leaf options and reflects an
 * indeterminate state in between. Members and each provider source are all this same shape.
 */
export interface MultiMapExportContentGroup<TValue> {
  /** Identifies the group in test ids and as a React key. Provider groups use their parent filter option. */
  readonly key: string;
  readonly label: string;
  readonly options: readonly MultiMapExportContentOption<TValue>[];
}

export type MultiMapExportProviderGroup = MultiMapExportContentGroup<ProvidersFilterOptions>;

export type MultiMapExportMemberGroup = MultiMapExportContentGroup<MultiMapExportMemberLayer>;

/** One specialty group of the run configuration's adequacy template, with the type the Specialty Type filter matches on. */
export interface MultiMapExportSpecialty {
  readonly code: string;
  readonly name: string;
  readonly specialtyType: SpecialtyTypeEnum;
}

/**
 * Named for the outcome the user sees, which is the requirement that FAILED. The existing QES counters are
 * named for the requirement that PASSED, so the two read inverted:
 * FailingServicingRequirement is AccessOnlyMetCount, FailingAccessRequirement is ServicingOnlyMetCount,
 * FailingBothRequirements is NoneMetCount, and Passing is the implicit remainder.
 */
export enum MultiMapExportAdequacyOutcome {
  FailingAccessRequirement = "failingAccessRequirement",
  FailingBothRequirements = "failingBothRequirements",
  FailingServicingRequirement = "failingServicingRequirement",
  Passing = "passing",
}

/** One analysed county/specialty-group combination, classified into a single adequacy outcome. */
export interface MultiMapExportAdequacyCombination {
  readonly outcome: MultiMapExportAdequacyOutcome;
  readonly regionSetId: number;
  readonly specialtyGroupCode: string;
}

export enum MultiMapExportBaseMap {
  Light = "light",
  Terrain = "terrain",
}

// Wire values bind to the C# MapCountyShading members by name.
export enum MultiMapExportCountyShading {
  AdequacyStatus = "adequacyStatus",
  CountyStatus = "countyStatus",
  NoShading = "noShading",
  SolidColor = "solidColor",
}

export enum MultiMapExportSolidFillColor {
  Blue = "blue",
  Green = "green",
  Lavender = "lavender",
  White = "white",
  Yellow = "yellow",
}

/** One swatch the county shading section shows: a fill and, for the status legends, an outline. */
export interface MultiMapExportShadingSwatch<TValue> {
  readonly borderColor?: string;
  readonly color: string;
  readonly label: string;
  readonly value: TValue;
}

export enum MultiMapExportZoomLevel {
  County = "county",
  MapContents = "mapContents",
}

export enum MultiMapExportBoundaryLayer {
  CountyBoundaries = "countyBoundaries",
  CountyLabels = "countyLabels",
  StateBoundaries = "stateBoundaries",
  StateLabels = "stateLabels",
  ZipCodeBoundaries = "zipCodeBoundaries",
  ZipCodeLabels = "zipCodeLabels",
  ZipCodePoints = "zipCodePoints",
}

export interface MultiMapExportBoundaryOption {
  readonly isChildRow?: boolean;
  readonly label: string;
  readonly layer: MultiMapExportBoundaryLayer;
}

export interface MultiMapExportBoundaryLabelOption extends MultiMapExportBoundaryOption {
  readonly dependsOn: MultiMapExportBoundaryLayer;
}

export interface MultiMapExportMapStyleOption<TValue> {
  readonly hoverDescription?: string;
  readonly label: string;
  readonly value: TValue;
}

/** What the delivered .zip contains. Every file structure is delivered as a .zip. Shown as "File Format". */
export enum MultiMapExportFileStructure {
  CollatedPdf = "collatedPdf",
  IndividualJpegs = "individualJpegs",
  IndividualPngs = "individualPngs",
}
